import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { StatusBadge, fmtDate } from "./StatusBadge";
import { NoteBadge } from "./NoteBadge";

const Verified = ({ ok }) => ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-label="Verified" /> : <XCircle className="h-4 w-4 text-muted-foreground" aria-label="Not verified" />;
const warn = (a) => (a.double_billing ? "bg-yellow-100 dark:bg-yellow-500/20" : "");
const DoubleBilling = ({ a }) => a.double_billing && (
  <span className="mt-1 flex items-center gap-1 text-xs font-medium text-yellow-800 dark:text-yellow-300"><AlertTriangle className="h-3 w-3" />Comped + paying in Stripe</span>
);

const HEAD = ["Company", "City/State", "Signed up", "Status", "Trial ends", "Leads", "Last login", "Email"];

const Table = ({ items }) => {
  const navigate = useNavigate();
  return (
    <div className="hidden overflow-x-auto rounded-2xl border bg-card shadow-soft md:block">
      <table className="w-full text-left text-sm" data-testid="admin-accounts-table">
        <thead className="border-b text-xs uppercase tracking-wider text-muted-foreground">
          <tr>{HEAD.map((h) => <th key={h} className="whitespace-nowrap px-3 py-3 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y">
          {items.map((a) => (
            <tr key={a.id} onClick={() => navigate(`/admin/accounts/${a.id}`)} className={`cursor-pointer transition-colors hover:bg-muted/50 ${warn(a)}`} data-testid={`admin-account-row-${a.id}`} data-double-billing={a.double_billing}>
              <td className="max-w-[240px] px-3 py-3">
                <p className="flex items-center gap-1.5"><span className="truncate font-medium text-foreground">{a.company_name}</span><NoteBadge notes={a.notes} id={a.id} /></p>
                <p className="truncate text-xs text-muted-foreground">{a.email}</p>
                <DoubleBilling a={a} />
              </td>
              <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">{a.city ? `${a.city}, ${a.state || ""}` : "—"}</td>
              <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">{fmtDate(a.created_at)}</td>
              <td className="px-3 py-3"><StatusBadge status={a.status} suspended={a.suspended} /></td>
              <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">{a.status === "comped" ? (a.comped_until ? `Comp to ${fmtDate(a.comped_until)}` : "Permanent") : fmtDate(a.trial_ends_at)}</td>
              <td className="px-3 py-3 font-medium text-foreground">{a.leads}</td>
              <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">{fmtDate(a.last_login_at)}</td>
              <td className="px-3 py-3"><Verified ok={a.email_verified} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Cards = ({ items }) => (
  <ul className="space-y-2.5 md:hidden" data-testid="admin-accounts-cards">
    {items.map((a) => (
      <li key={a.id}>
        <Link to={`/admin/accounts/${a.id}`} className={`block rounded-2xl border bg-card p-4 shadow-soft ${warn(a)}`} data-testid={`admin-account-card-${a.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5"><span className="truncate font-semibold text-foreground">{a.company_name}</span><NoteBadge notes={a.notes} id={`m-${a.id}`} /></p>
              <p className="truncate text-xs text-muted-foreground">{a.email}</p>
            </div>
            <StatusBadge status={a.status} suspended={a.suspended} />
          </div>
          <DoubleBilling a={a} />
          <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span>{a.city ? `${a.city}, ${a.state || ""}` : "No location"}</span><span>Joined {fmtDate(a.created_at)}</span>
            <span>{a.leads} leads</span><span>Login {fmtDate(a.last_login_at)}</span><span>{a.email_verified ? "Verified" : "Unverified"}</span>
          </p>
        </Link>
      </li>
    ))}
  </ul>
);

export const AccountsList = ({ items }) => items.length === 0
  ? <p className="rounded-2xl border border-dashed bg-card p-8 text-center text-sm text-muted-foreground" data-testid="admin-accounts-empty">No accounts match these filters.</p>
  : <><Table items={items} /><Cards items={items} /></>;
